import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  PawPrint,
  BellRing,
  MapPin,
  ShieldCheck,
  Share2,
  ChevronRight,
  Check,
  Sparkles,
  HeartHandshake,
  AlertTriangle,
  FileLock2,
} from "lucide-react";
import { orioIllustration, orioIllustrationFallback } from "@/lib/illustrations";
import IlloImg from "@/components/IlloImg";

const STEPS = [
  {
    id: "welcome",
    kind: "intro",
    eyebrow: "Benvenuto",
    title: "Benvenuto in ORIO",
    body: "La rete che protegge, ama e connette il tuo cane. Bastano 90 secondi per configurarti — poi sei pronto.",
    cta: "Iniziamo",
    illo: "hero-mobile",
    accent: "#FF7A1A",
    icon: Sparkles,
  },
  {
    id: "pillars",
    kind: "intro",
    eyebrow: "Cosa fa ORIO",
    title: "4 servizi in un'app sola",
    body: "SOS in emergenza, missioni di soccorso della community, match per passeggiate, cassetto digitale con QR passaporto.",
    cta: "Ho capito",
    illo: "feat-sos",
    accent: "#FF3B30",
    icon: ShieldCheck,
    pillars: [
      { icon: AlertTriangle, label: "SOS", color: "#FF3B30" },
      { icon: MapPin, label: "Missioni", color: "#FF7A1A" },
      { icon: HeartHandshake, label: "Match", color: "#2DBA60" },
      { icon: FileLock2, label: "Cassetto", color: "#7B61FF" },
    ],
  },
  {
    id: "dog",
    eyebrow: "Il tuo compagno",
    title: "Aggiungi il tuo cane",
    body: "Nome, razza, peso e una foto. La scheda è il centro di tutta l'esperienza ORIO.",
    cta: "Aggiungi cane",
    illo: "feat-create-dog",
    accent: "#FF7A1A",
    href: "/app/cani/nuovo",
    icon: PawPrint,
  },
  {
    id: "notifs",
    eyebrow: "Emergenze in tempo reale",
    title: "Attiva le notifiche di sicurezza",
    body: "Ricevi un avviso solo per quello che conta: SOS vicini, pericoli entro 5 km, missioni urgenti in zona.",
    cta: "Attiva notifiche",
    illo: "feat-sos",
    accent: "#FF3B30",
    action: "notifications",
    icon: BellRing,
  },
  {
    id: "geo",
    eyebrow: "La tua zona",
    title: "Attiva la geolocalizzazione",
    body: "Serve per mostrarti pericoli e missioni vicino a te. La tua posizione precisa non viene mai condivisa senza il tuo consenso.",
    cta: "Consenti posizione",
    illo: "feat-walk",
    accent: "#34D399",
    action: "geo",
    icon: MapPin,
  },
  {
    id: "trial",
    eyebrow: "30 giorni gratis",
    title: "ORIO Pro è già attivo per te",
    body: "Cassetto digitale, Match passeggiate, Missioni, SOS geolocalizzati e assistenza premium. Nessun addebito fino al 31° giorno.",
    cta: "Continua",
    illo: "pro-banner",
    accent: "#F4B83C",
    icon: ShieldCheck,
  },
  {
    id: "invite",
    eyebrow: "Cresci la rete",
    title: "Invita chi ha un cane",
    body: "Più padroni ORIO in zona = più occhi in strada. Con il referral, entrambi ottenete 30 giorni extra di Pro.",
    cta: "Invita un amico",
    illo: "feat-match",
    accent: "#7B61FF",
    action: "share",
    icon: Share2,
  },
];

export default function OnboardingChecklist({ open, onClose }) {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [done, setDone] = useState({});
  const step = STEPS[idx];
  const isLast = idx === STEPS.length - 1;

  const next = () => {
    setDone((d) => ({ ...d, [step.id]: true }));
    if (isLast) onClose?.();
    else setIdx((i) => i + 1);
  };

  const skip = () => {
    if (isLast) onClose?.();
    else setIdx((i) => i + 1);
  };

  const runAction = async () => {
    if (step.href) {
      onClose?.();
      navigate(step.href);
      return;
    }
    if (step.action === "notifications") {
      try {
        if ("Notification" in window && Notification.permission === "default") {
          await Notification.requestPermission();
        }
      } catch { /* ignore */ }
      next();
      return;
    }
    if (step.action === "geo") {
      if (!navigator.geolocation) return next();
      navigator.geolocation.getCurrentPosition(
        () => next(),
        () => next(),
        { enableHighAccuracy: true, timeout: 6000 }
      );
      return;
    }
    if (step.action === "share") {
      const shareUrl = `${window.location.origin}/?ref=welcome`;
      const text = "Prova ORIO — la rete che protegge, ama e connette il nostro cane.";
      try {
        if (navigator.share) {
          await navigator.share({ title: "ORIO", text, url: shareUrl });
        } else {
          await navigator.clipboard.writeText(shareUrl);
        }
      } catch { /* ignore */ }
      next();
      return;
    }
    next();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="onboarding-checklist-overlay"
          className="fixed inset-0 z-[1500] flex items-stretch justify-center"
          style={{ background: "#FBF7F1" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Progress dots + Skip */}
          <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-5 pt-4 sm:pt-6">
            <div className="flex items-center gap-1.5" data-testid="onboarding-dots">
              {STEPS.map((s, i) => (
                <span
                  key={s.id}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: i === idx ? 28 : 14,
                    background: i <= idx ? "#FF7A1A" : "#EAE3D2",
                  }}
                />
              ))}
            </div>
            <button
              data-testid="onboarding-skip-all"
              onClick={onClose}
              className="text-[12px] text-[#7A7A7A] px-3 py-1.5 rounded-full hover:bg-white/60 transition-colors"
            >
              Salta tutto
            </button>
          </div>

          {/* Slide */}
          <div className="w-full max-w-md mx-auto flex flex-col justify-between pt-16 pb-6 px-5">
            <AnimatePresence mode="wait">
              <motion.div
                key={step.id}
                data-testid={`onboarding-step-${step.id}`}
                initial={{ x: 40, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -40, opacity: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="flex flex-col items-center text-center"
              >
                {/* Illustration */}
                <div
                  className="relative w-full rounded-3xl overflow-hidden"
                  style={{
                    aspectRatio: step.kind === "intro" ? "3 / 4" : "1 / 1",
                    maxWidth: 340,
                    background: `${step.accent}15`,
                    boxShadow: "0 20px 50px -20px rgba(0,0,0,0.15)",
                    border: "1px solid #F0EBE3",
                  }}
                >
                  <IlloImg slug={step.illo} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  {/* Number pill */}
                  <div
                    className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[9.5px] uppercase tracking-[0.18em] font-bold"
                    style={{ color: step.accent, boxShadow: "0 4px 12px -4px rgba(0,0,0,0.15)" }}
                  >
                    <step.icon className="h-3 w-3" />
                    {step.eyebrow}
                  </div>
                  {done[step.id] && (
                    <div
                      className="absolute top-3 right-3 h-7 w-7 rounded-full flex items-center justify-center text-white"
                      style={{ background: "#2DBA60" }}
                    >
                      <Check className="h-4 w-4" />
                    </div>
                  )}
                  {/* Bottom tint for legibility on intro slides */}
                  {step.kind === "intro" && (
                    <div
                      aria-hidden
                      className="absolute inset-x-0 bottom-0 h-[45%]"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(251,247,241,0) 0%, rgba(251,247,241,0.6) 55%, rgba(251,247,241,0.92) 100%)",
                      }}
                    />
                  )}
                </div>

                {/* Text */}
                <h2 className="mt-6 font-display font-bold text-[26px] sm:text-[28px] leading-[1.1] tracking-tight text-[#0F0F0F]">
                  {step.title}
                </h2>
                <p className="mt-3 text-[14px] text-[#5C5C5C] leading-relaxed max-w-[420px]">
                  {step.body}
                </p>

                {/* Pillar grid — only for the "pillars" intro slide */}
                {step.pillars && (
                  <div className="mt-5 grid grid-cols-4 gap-2 w-full max-w-[340px]">
                    {step.pillars.map((p) => (
                      <div
                        key={p.label}
                        className="rounded-2xl bg-white border border-[#F0EBE3] p-2.5 flex flex-col items-center gap-1.5"
                        style={{ boxShadow: "0 4px 12px -6px rgba(0,0,0,0.08)" }}
                      >
                        <div
                          className="h-8 w-8 rounded-xl flex items-center justify-center text-white"
                          style={{
                            background: `linear-gradient(135deg, ${p.color} 0%, ${p.color}CC 100%)`,
                            boxShadow: `0 4px 10px -3px ${p.color}66`,
                          }}
                        >
                          <p.icon className="h-4 w-4" />
                        </div>
                        <div className="text-[10.5px] font-semibold text-[#1A1A1A]">{p.label}</div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* CTAs */}
            <div className="mt-6 flex flex-col gap-2">
              <button
                data-testid="onboarding-primary-cta"
                onClick={runAction}
                className="w-full px-5 py-3.5 rounded-full font-semibold text-white text-[14px] active:scale-[0.98] transition-transform inline-flex items-center justify-center gap-2"
                style={{
                  background: `linear-gradient(135deg, ${step.accent} 0%, ${step.accent}DD 100%)`,
                  boxShadow: `0 12px 26px -10px ${step.accent}88`,
                }}
              >
                {step.cta}
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                data-testid="onboarding-skip-step"
                onClick={skip}
                className="w-full px-5 py-2.5 rounded-full font-medium text-[13px] text-[#7A7A7A]"
              >
                {isLast ? "Fine" : "Più tardi"}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
